import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getMatch } from "@/lib/api";
import { Activity, RefreshCw, ArrowLeft } from "lucide-react";

const REFRESH_MS = 5000;

export default function Spectator() {
  const { id } = useParams();
  const nav = useNavigate();
  const [match, setMatch] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const lastFetchRef = useRef(0);

  useEffect(() => {
    let timer;
    let cancelled = false;
    const tick = async () => {
      try {
        setRefreshing(true);
        const m = await getMatch(id);
        if (cancelled) return;
        setMatch(m);
        setError(null);
        lastFetchRef.current = Date.now();
      } catch (e) {
        if (cancelled) return;
        setError("Couldn't reach the server");
      } finally {
        if (!cancelled) setRefreshing(false);
      }
      timer = setTimeout(tick, REFRESH_MS);
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [id]);

  if (!match) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
            data-testid="spectator-back-btn"
            className="tactical-btn border border-border hover:border-primary hover:text-primary inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em]"
          >
            <ArrowLeft className="w-3 h-3" strokeWidth={3} /> Back
          </button>
          <Link to="/" className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground hover:text-primary">
            CrickPad Home →
          </Link>
        </div>
        <div className="p-12 text-center">
          <p className="text-muted-foreground uppercase tracking-[0.3em] text-xs">
            {error || "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  const currentInning = match.innings[match.current_innings];
  const summary = currentInning?.summary;
  const battingTeam = match.teams[currentInning?.batting_team_idx];
  const bowlingTeam = match.teams[currentInning?.bowling_team_idx];
  const fmt = match.format;
  const lastInningsIdx = fmt === "Test" ? 3 : 1;
  const isLast = match.current_innings === lastInningsIdx && match.status === "live";

  let target = null;
  if (isLast) {
    let opposing = 0, own = 0;
    for (let i = 0; i < match.current_innings; i++) {
      const r = match.innings[i].summary.runs;
      if (match.innings[i].batting_team_idx === currentInning.batting_team_idx) own += r;
      else opposing += r;
    }
    target = opposing - own + 1;
  }
  const ballsRemaining = fmt !== "Test" && summary
    ? Math.max(0, match.overs * 6 - summary.legal_balls) : null;
  const required = target !== null && summary !== undefined ? target - summary.runs : null;
  const reqRR = target && ballsRemaining ? ((required / ballsRemaining) * 6).toFixed(2) : null;
  const currentRR = summary && summary.legal_balls > 0
    ? ((summary.runs / summary.legal_balls) * 6).toFixed(2) : "0.00";

  // Active players
  const striker = battingTeam?.players.find((p) => p.id === currentInning?.striker_id);
  const nonStriker = battingTeam?.players.find((p) => p.id === currentInning?.non_striker_id);
  const bowler = bowlingTeam?.players.find((p) => p.id === currentInning?.bowler_id);
  const strikerStats = striker ? currentInning.batsmen_stats.find((b) => b.player_id === striker.id) : null;
  const nonStrikerStats = nonStriker ? currentInning.batsmen_stats.find((b) => b.player_id === nonStriker.id) : null;
  const bowlerStats = bowler ? currentInning.bowler_stats.find((b) => b.player_id === bowler.id) : null;

  // Last 12 balls
  const recentBalls = (currentInning?.balls || []).slice(-12);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6 pb-20" data-testid="spectator-page">
      {/* TOP CONTROLS */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
          data-testid="spectator-back-btn"
          className="tactical-btn border border-border hover:border-primary hover:text-primary inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em]"
        >
          <ArrowLeft className="w-3 h-3" strokeWidth={3} /> Back
        </button>
        <Link
          to="/"
          data-testid="spectator-home-link"
          className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground hover:text-primary"
        >
          CrickPad Home →
        </Link>
      </div>

      {/* HEADER */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {match.status === "live" ? (
            <>
              <span className="pulse-dot"></span>
              <span className="text-[10px] uppercase tracking-[0.3em] text-destructive font-bold">Live</span>
            </>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.3em] text-primary font-bold">Final</span>
          )}
          <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{match.format}</span>
        </div>
        <div className="flex items-center gap-2">
          {refreshing && <RefreshCw className="w-3 h-3 text-muted-foreground animate-spin" />}
          <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            Auto · {REFRESH_MS / 1000}s
          </span>
        </div>
      </div>

      {/* SCOREBOARD */}
      <div className="border border-border bg-card p-5 mb-4 relative overflow-hidden">
        <div className="absolute -top-px -right-px w-12 h-12 border-t-2 border-r-2 border-primary"></div>
        <div className="absolute -bottom-px -left-px w-12 h-12 border-b-2 border-l-2 border-primary"></div>

        {match.status === "completed" ? (
          <>
            <div className="space-y-3 mb-4">
              {match.innings.map((inn, idx) => (
                <div key={idx} className="flex items-baseline justify-between">
                  <span className="font-display uppercase font-black text-xl truncate pr-3">
                    {inn.batting_team_name}
                  </span>
                  <span className="font-mono text-base stat-tabular">
                    {inn.summary.runs}/{inn.summary.wickets} <span className="text-muted-foreground text-sm">({inn.summary.overs})</span>
                  </span>
                </div>
              ))}
            </div>
            {match.result && (
              <div className="pt-4 border-t border-border">
                <p className="font-display uppercase font-black text-xl text-primary leading-tight">{match.result}</p>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">{battingTeam?.name}</div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="font-display font-black text-6xl sm:text-7xl leading-none stat-tabular">
                {summary?.runs || 0}
              </span>
              <span className="font-display font-black text-3xl text-muted-foreground stat-tabular">
                /{summary?.wickets || 0}
              </span>
              <span className="font-mono text-sm text-muted-foreground stat-tabular">({summary?.overs})</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs">
              <span className="text-muted-foreground uppercase tracking-[0.2em]">CRR <span className="text-foreground font-mono ml-1 stat-tabular">{currentRR}</span></span>
              {target !== null && (
                <>
                  <span className="text-muted-foreground uppercase tracking-[0.2em]">Target <span className="text-primary font-mono ml-1 stat-tabular">{target}</span></span>
                  {reqRR && (
                    <span className="text-muted-foreground uppercase tracking-[0.2em]">RRR <span className="text-destructive font-mono ml-1 stat-tabular">{reqRR}</span></span>
                  )}
                </>
              )}
            </div>
            {target !== null && required !== null && ballsRemaining !== null && (
              <p className="mt-3 text-xs text-muted-foreground">
                Need <span className="text-primary font-bold stat-tabular">{required}</span> off{" "}
                <span className="text-primary font-bold stat-tabular">{ballsRemaining}</span> balls
              </p>
            )}
          </>
        )}
      </div>

      {/* ACTIVE PLAYERS */}
      {match.status === "live" && (
        <div className="border border-border bg-card p-5 mb-4">
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold mb-3 flex items-center gap-2">
            <Activity className="w-3 h-3 text-primary" /> At The Crease
          </div>
          <div className="space-y-2">
            <SpectatorBatsman name={striker?.name} stats={strikerStats} striker />
            <SpectatorBatsman name={nonStriker?.name} stats={nonStrikerStats} />
          </div>
          <div className="mt-4 pt-3 border-t border-border">
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold mb-2">Bowling</div>
            <SpectatorBowler name={bowler?.name} stats={bowlerStats} />
          </div>
        </div>
      )}

      {/* RECENT BALLS */}
      {match.status === "live" && recentBalls.length > 0 && (
        <div className="border border-border bg-card p-5 mb-4">
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold mb-3">Recent Balls</div>
          <div className="flex flex-wrap gap-2">
            {recentBalls.map((b, i) => (
              <SpecBallChip key={i} ball={b} />
            ))}
          </div>
        </div>
      )}

      {/* MATCH INFO */}
      <div className="border border-border bg-card p-5">
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold mb-3">Match Info</div>
        <dl className="space-y-2 text-sm font-mono">
          <Row k="Teams" v={`${match.teams[0].name} vs ${match.teams[1].name}`} />
          <Row k="Format" v={match.format} />
          <Row k="Overs" v={match.overs} />
          <Row k="Toss" v={`${match.teams[match.toss_winner === "A" ? 0 : 1].name} (${match.toss_decision})`} />
          {match.venue && <Row k="Venue" v={match.venue} />}
        </dl>
      </div>

      <p className="text-center text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-6">
        Powered by CrickPad · Read-only view
      </p>
    </div>
  );
}

function SpectatorBatsman({ name, stats, striker }) {
  if (!name) return <div className="text-sm text-muted-foreground italic">—</div>;
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className={`w-1.5 h-1.5 rounded-full ${striker ? "bg-primary" : "bg-transparent border border-border"}`}></span>
        <span className="font-display uppercase font-bold text-sm tracking-tight">{name}</span>
        {striker && <span className="text-[10px] text-primary font-mono">*</span>}
      </div>
      <div className="font-mono text-sm flex items-center gap-2 stat-tabular">
        <span className="text-foreground font-bold">{stats?.runs ?? 0}</span>
        <span className="text-muted-foreground">({stats?.balls ?? 0})</span>
      </div>
    </div>
  );
}

function SpectatorBowler({ name, stats }) {
  if (!name) return <div className="text-sm text-muted-foreground italic">—</div>;
  return (
    <div className="flex items-center justify-between">
      <span className="font-display uppercase font-bold text-sm tracking-tight">{name}</span>
      <div className="font-mono text-sm flex items-center gap-2 stat-tabular">
        <span>{stats?.overs ?? "0.0"}</span>
        <span className="text-muted-foreground">·</span>
        <span>{stats?.runs ?? 0}/{stats?.wickets ?? 0}</span>
      </div>
    </div>
  );
}

function SpecBallChip({ ball }) {
  let label = String(ball.runs);
  let color = "border-border text-foreground";
  if (ball.is_wicket) { label = "W"; color = "bg-destructive text-destructive-foreground border-destructive"; }
  else if (ball.extras_type === "wide") { label = `WD${ball.extras_runs > 1 ? `+${ball.extras_runs - 1}` : ""}`; color = "border-primary text-primary"; }
  else if (ball.extras_type === "no_ball") { label = `NB${ball.runs ? `+${ball.runs}` : ""}`; color = "border-primary text-primary"; }
  else if (ball.extras_type === "bye") { label = `B${ball.extras_runs}`; color = "border-border text-muted-foreground"; }
  else if (ball.extras_type === "leg_bye") { label = `LB${ball.extras_runs}`; color = "border-border text-muted-foreground"; }
  else if (ball.runs === 4 || ball.runs === 6) { color = "bg-primary text-primary-foreground border-primary"; }
  return (
    <div className={`min-w-[36px] h-9 px-2 flex items-center justify-center font-display font-black text-xs border ${color}`}>
      {label}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-border pb-1 last:border-0">
      <dt className="text-muted-foreground uppercase text-[10px] tracking-[0.2em]">{k}</dt>
      <dd className="text-foreground">{v}</dd>
    </div>
  );
}
